/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  GamoraControllerContextIdentifier,
  GamoraFoundationAdapter,
  GamoraStoreDefaultNodeArgs,
  GamoraStoreDefaultNodeConfiguration,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import { GamoraUUIDGuard } from '@ver.id/libs.internal.gamora';
import { HoruzProtocolChulakServiceSessionGuard } from '../guards/service-session.js';
import { HoruzProtocolChulakStoreBaseNode } from './base.js';
import { IHoruz } from '../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:stores:service',
});

/**
 * Args
 * @public
 */
export type HoruzProtocolChulakStoreChulakServiceSessionNodeArgs<
  TRoot extends IHoruz,
> = GamoraStoreDefaultNodeArgs<GamoraFoundationAdapter, never>;

/**
 * Args
 * @public
 */
export interface HoruzProtocolChulakStoreChulakServiceSessionConfiguration extends GamoraStoreDefaultNodeConfiguration {
  maxContextIdentifiersPerSession: number;
}

/**
 * @public
 */
export const HoruzProtocolChulakStoreChulakServiceSessionDefaultNamespace =
  'protocols/chulak/store/service/v1';

/**
 * HoruzProtocolChulakStoreChulakServiceSessionNode
 * @public
 */
export class HoruzProtocolChulakStoreChulakServiceSessionNode<
  TRoot extends IHoruz,
> extends HoruzProtocolChulakStoreBaseNode<
  HoruzProtocolChulakServiceSessionGuard,
  TRoot,
  never
> {
  protected _contextIdentifierByUuidMap: Map<
    string,
    Set<GamoraControllerContextIdentifier>
  >;
  protected override _configuration: HoruzProtocolChulakStoreChulakServiceSessionConfiguration =
    {
      defaultLockTimeToLive: 30 * 1000,
      defaultLockWaitRetryCount: 3,
      defaultLockWaitRetryTime: 1000,
      maxContextIdentifiersPerSession: 5,
    };

  /**
   * Constructor
   */
  constructor(
    args: HoruzProtocolChulakStoreChulakServiceSessionNodeArgs<TRoot>,
  ) {
    super(args);

    // Uuid map
    this._contextIdentifierByUuidMap = new Map();
  }

  /**
   * Lock resource
   */
  async lock(serviceSessionUuid: GamoraUUIDGuard): Promise<boolean> {
    assert(
      serviceSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serviceSessionUuid argument: not a GamoraUUIDGuard.',
    );
    return await this.adapter.execute(
      this.adapter.lock(
        HoruzProtocolChulakStoreChulakServiceSessionDefaultNamespace,
        serviceSessionUuid,
        this._configuration.defaultLockTimeToLive,
      ),
    );
  }

  /**
   * Unlock resource
   */
  async unlock(serviceSessionUuid: GamoraUUIDGuard): Promise<boolean> {
    assert(
      serviceSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serviceSessionUuid argument: not a GamoraUUIDGuard.',
    );
    return await this.adapter.execute(
      this.adapter.unlock(
        HoruzProtocolChulakStoreChulakServiceSessionDefaultNamespace,
        serviceSessionUuid,
      ),
    );
  }

  /**
   * Has
   */
  async has(serviceSessionUuid: GamoraUUIDGuard) {
    assert(
      serviceSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serviceSessionUuid argument: not a GamoraUUIDGuard.',
    );
    return this.adapter.execute(
      this.adapter.has(
        HoruzProtocolChulakStoreChulakServiceSessionDefaultNamespace,
        serviceSessionUuid,
      ),
    );
  }

  /**
   * Get
   */
  async get(
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<HoruzProtocolChulakServiceSessionGuard> {
    assert(
      serviceSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serviceSessionUuid argument: not a GamoraUUIDGuard.',
    );

    // Get protocol state
    const serviceSession = await this.adapter.execute(
      this.adapter.get(
        HoruzProtocolChulakStoreChulakServiceSessionDefaultNamespace,
        serviceSessionUuid,
      ),
    );

    debug.log(
      '(get): Get serviceSessionUuid: ' + serviceSessionUuid.toJSON() + '.',
    );

    // Return
    return HoruzProtocolChulakServiceSessionGuard.fromJSON(
      JSON.parse(serviceSession),
    );
  }

  /**
   * Remove
   */
  async remove(serviceSessionUuid: GamoraUUIDGuard): Promise<void> {
    assert(
      serviceSessionUuid instanceof GamoraUUIDGuard,
      'Invalid serviceSessionUuid argument: not a GamoraUUIDGuard.',
    );

    // Get protocol state
    await this.adapter.execute(
      this.adapter.remove(
        HoruzProtocolChulakStoreChulakServiceSessionDefaultNamespace,
        serviceSessionUuid,
      ),
    );

    debug.log(
      '(remove): Remove serviceSessionUuid: ' +
        serviceSessionUuid.toJSON() +
        '.',
    );
  }

  /**
   * Upsert
   */
  async upsert(
    serviceSession: HoruzProtocolChulakServiceSessionGuard,
  ): Promise<void> {
    assert(
      serviceSession instanceof HoruzProtocolChulakServiceSessionGuard,
      'Invalid serviceSession argument: not a HoruzProtocolChulakServiceSessionGuard.',
    );

    // Upsert
    await this.adapter.execute(
      this.adapter.upsert(
        HoruzProtocolChulakStoreChulakServiceSessionDefaultNamespace,
        serviceSession.uuid,
        JSON.stringify(serviceSession.toJSON()),
      ),
    );

    debug.log(
      '(upsert): Upserted serviceSession with uuid: ' +
        serviceSession.uuid.toJSON() +
        '.',
    );
  }
}
